import { Component } from '@theme/component';
import { cartForUpdate } from '@theme/echt-cart-response';
import { swapCartSize } from '@theme/echt-cart-size-swap';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig, lockScroll, unlockScroll } from '@theme/utilities';
import { CartDiscountUpdateEvent, CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const TOAST_MS = 6000;

/**
 * `<echt-cart-drawer>` — the Echt cart drawer's dialog, plus the parts of the
 * panel that have no stock owner.
 *
 * This owns the dialog rather than riding stock `<theme-drawer>`. That element
 * squeezes page content beside the panel above 990px (`MODAL_BREAKPOINT`, via
 * `panel.show()`) and only goes modal below it, so on desktop there is no
 * scrim, no inert page and a 480px gutter that is not our 420. The brief has no
 * breakpoint in it — slide in from the right, scrim 0 -> 50%, body locked,
 * focus trapped, Esc and scrim tap close — and `showModal()` at every width
 * gives the top layer, the focus trap and page inertness natively. Stock
 * `theme-drawer.js` is untouched; the chat drawer still uses it.
 *
 * What we give up with it is stock's session restore of an open drawer across
 * navigation. The brief does not ask for it, and a bag that reopens itself on
 * every page is not obviously wanted.
 *
 * Everything below the dialog is still stock: `<cart-items-component>` owns
 * line quantity, line removal and the Section Rendering API re-render. What
 * this file adds beyond the dialog is the size sheet, the package protection
 * plate, the cross-sell rail's add and — since 7 Sep 2026 — the discount code
 * field, `<echt-cart-discount>` at the bottom of this file.
 *
 * There is no client-side recompute of the progress bar, the summary or the
 * button total. Every mutation here resolves a `CartLinesUpdateEvent` carrying
 * the freshly rendered section, which `<cart-items-component>` morphs into the
 * panel — so those numbers are always Shopify's, computed server-side, and the
 * drawer can never quote a total that checkout will disagree with.
 *
 * The section renders completely without this file: the checkout button is a
 * real submit on `#cart-form`, every product link is a real link, and the size
 * sheet's guide link goes to the product page.
 *
 * @typedef {object} EchtCartDrawerRefs
 * @property {HTMLDialogElement} dialog - The panel. A direct child, so it is reachable as a ref.
 * @property {HTMLElement} toast - The undo toast's body, shown by clearing `hidden`.
 * @property {HTMLElement} toastText - The toast's message, replaced on every removal.
 *
 * @typedef {object} RemovedLine
 * @property {string} id - The removed line's variant id.
 * @property {number} quantity - The quantity it held.
 * @property {string} title - The product name, for the toast's message.
 *
 * @typedef {object} SheetVariant
 * @property {number} id - Variant id.
 * @property {string} size - The size option's value.
 * @property {string | null} length - The length option's value, or null when the product has one axis.
 * @property {boolean} available - Whether the variant can be bought.
 *
 * @extends {Component<EchtCartDrawerRefs>}
 */
class EchtCartDrawer extends Component {
  requiredRefs = ['dialog'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** Listeners for the current connected lifetime. Re-made on every connect:
      a controller built once at construction stays aborted after the first
      disconnect, so a reconnected element would silently lose its listeners. */
  #controller = new AbortController();

  /** The element focus returns to when the drawer closes. @type {HTMLElement | null} */
  #previouslyFocused = null;

  /** The line the last Remove took out, or null once the toast has gone. @type {RemovedLine | null} */
  #removed = null;

  #removingLine = false;
  #changingSize = false;
  #sizeFeedbackObserver = null;
  #sizeFeedbackScrollController;
  #sizeFeedbackTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #toastTimer;

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const { dialog } = this.refs;

    // Esc: the native default closes without running the exit transition, so we
    // take it over and go through close() like every other path.
    dialog.addEventListener('cancel', this.#onCancel, { signal });
    dialog.addEventListener('click', this.#onDialogClick, { signal });
    dialog.addEventListener('close', this.#onDialogClose, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate, { signal });
    // Every cart write from anywhere else — the stock quantity stepper, a PDP
    // add, the cart page — so a bag that drops under the gift line loses its
    // gift however it got there. This component's own writes are checked in
    // #updateCart, on the response they already hold.
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onForeignCartWrite, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#nudgeRedo, { signal });

    // The toast dismisses itself on a timer, but not while the shopper is on
    // it: a keyboard user who has just tabbed to Undo must not have it pulled
    // out from under them. The countdown restarts when focus leaves.
    const { toast } = this.refs;
    toast?.addEventListener('focusin', () => clearTimeout(this.#toastTimer), { signal });
    toast?.addEventListener('focusout', () => this.#armToastTimer(), { signal });

    // The gift picker's cards are native <form action="/cart/add"> so they work
    // with scripting off on the cart page. In the drawer a submit would
    // navigate away; take it over and go through the one write path instead.
    this.addEventListener('submit', this.#onGiftSubmit, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#sizeFeedbackTimer);
    this.#sizeFeedbackObserver?.disconnect();
    this.#sizeFeedbackScrollController?.abort();

    this.#controller.abort();
    this.#activeFetch?.abort();
    this.#activeFetch = null;
    clearTimeout(this.#toastTimer);
    unlockScroll(this);
  }

  /**
   * @returns {boolean} Whether the drawer is open.
   */
  get isOpen() {
    return this.refs.dialog.open;
  }

  /**
   * Opens the drawer. Public and stable: `assets/echt-header.js` reaches for
   * `document.getElementById('cart-drawer').open()` to open the bag, and the
   * visual gate's opener drives this same method rather than a test-only hook.
   */
  open() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    this.#previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    dialog.showModal();
    lockScroll(this);

    // The one place a /cart.js read for the code chips can be paid for: the
    // shopper has just asked to see the bag. A code the cart is HOLDING but not
    // applying produces no discount application anywhere in the `cart` Liquid
    // object, so it cannot be rendered server-side on any page load — see
    // EchtCartDiscount#refresh. Fires at most once per page; our own writes keep
    // the set current after that.
    /** @type {any} */ (this.querySelector('echt-cart-discount'))?.refresh();
  }

  /**
   * Closes the drawer and returns focus to whatever opened it.
   */
  close() {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    dialog.close();
  }

  /**
   * Closes the drawer to let a foreign overlay be seen, and says whether it had
   * to.
   *
   * Why this exists at all: `open()` uses `showModal()` at every width, which
   * promotes the dialog into the browser's **top layer**. Nothing outside the
   * top layer can ever paint above it — z-index does not apply across that
   * boundary — so BOGOS's popup (`#freegifts-main-popup-container`, a plain
   * `div` the app appends to `<body>`) is unreachable while the drawer is open,
   * whatever CSS says. The only fix that keeps `showModal()` (decision #10:
   * scrim, page inertness and the focus trap all come free with it) is to yield
   * the top layer: close, let the popup show, reopen afterwards. That
   * choreography belongs to `snippets/echt-bogos-sync.liquid`, the one file
   * that knows about the app; this method is the part only the drawer can do.
   *
   * The difference from `close()` is focus. `#onDialogClose` hands focus back
   * to whatever opened the drawer (the cart icon), which would pull the shopper
   * out of the popup that is appearing in the same frame. Dropping the trigger
   * first makes the close focus-neutral and leaves the popup free to take
   * focus; the caller reopens through `syncExternalCartWrite({open: true})`,
   * and `open()` records a fresh trigger then.
   *
   * @returns {boolean} Whether the drawer was open, i.e. whether the caller
   *   owes it a reopen once the overlay is gone.
   */
  closeForOverlay() {
    if (!this.isOpen) return false;

    this.#previouslyFocused = null;
    this.close();

    return true;
  }

  #onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /**
   * A click whose target is the dialog itself landed on the backdrop — the
   * panel's own content fills it edge to edge, so nothing else can produce one.
   *
   * @param {MouseEvent} event
   */
  #onDialogClick = (event) => {
    if (event.target !== this.refs.dialog) return;

    this.close();
  };

  #onDialogClose = () => {
    unlockScroll(this);
    this.closeSheet();
    this.dismissToast();

    const trigger = this.#previouslyFocused;
    this.#previouslyFocused = null;

    if (trigger?.isConnected) trigger.focus();

    for (const control of document.querySelectorAll('[aria-controls="cart-drawer"]')) {
      control.setAttribute('aria-expanded', 'false');
    }
  };

  /**
   * Auto-open on add-to-cart, which `<cart-drawer-component>` used to give us
   * for free. Gated on the same theme setting, and deferred when the add came
   * from inside another modal dialog (quick-add) so that dialog's own focus
   * restoration runs before we take the top layer.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #onCartLinesUpdate = (event) => {
    if (!this.hasAttribute('data-auto-open')) return;
    if (event.action !== 'add' || this.isOpen) return;
    if (event.target instanceof Element && this.contains(event.target)) return;

    const sourceModal =
      event.target instanceof Element
        ? /** @type {HTMLDialogElement | null} */ (event.target.closest('dialog:modal'))
        : null;

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', () => this.open(), { once: true });
        } else {
          this.open();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[echt-cart-drawer] Auto-open skipped:', error);
      });
  };

  /**
   * Tells ReDo the bag changed, once the mutation has actually landed.
   *
   * ReDo's returns/package-protection toggle attaches to
   * `.cart-drawer-form__checkout-buttons` inside the plate (see the section's
   * footer comment). That element lives inside `<cart-items-component>`, which
   * morphs the whole subtree on every cart change — so ReDo's placed
   * `<redo-shopify-toggle>` is thrown away several times a session, and its
   * price depends on a cart total that has just moved.
   *
   * ReDo does recover on its own: with a checkout-button attachment strategy
   * (this store's is "checkout-button-product") it keeps a MutationObserver on
   * `document` and re-places, debounced. Keep its redraw signal so a newly
   * morphed footer is populated promptly. ReDo observes product-form adds,
   * so forcing another reload there duplicates two reads. Other writers keep
   * the explicit reload: without it, quantity edits left a stale ReDo total
   * in the development regression test (14 Sep 2026).
   *
   * @param {CartLinesUpdateEvent} event
   */
  #nudgeRedo = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        if (detail?.source !== 'product-form-component') {
          document.dispatchEvent(new CustomEvent('redo:reload-cart'));
        }
        document.dispatchEvent(new CustomEvent('redo:re-render-elements'));
      })
      .catch(() => {});
  };

  /**
   * @param {CartLinesUpdateEvent} event
   */
  #onForeignCartWrite = (event) => {
    if (!this.dataset.giftThreshold) return;
    event.promise
      ?.then(async ({ detail }) => {
        if (detail?.didError || detail?.source === 'echt-cart-drawer') return;
        // Product forms have already read a complete AJAX cart before resolving
        // this event. Reuse it; other writers retain the canonical-read fallback.
        await this.#dropLapsedGift(await cartForUpdate(detail));
      })
      .catch(() => {});
  };

  /**
   * The size-sheet dock, found by query rather than by ref. The dock sits inside
   * `<cart-items-component>`, and a ref belongs to the NEAREST enclosing
   * component — so reading it off this element resolves against that one
   * instead and is always undefined.
   *
   * @returns {HTMLElement | null}
   */
  get #sheets() {
    return this.querySelector('.echt-cart-drawer__sheets');
  }

  /**
   * Reveals one size-sheet panel and hides the rest. Only one is ever open.
   *
   * @param {string} sheetId - The panel key, e.g. `l1` or `r2`.
   */
  openSheet(sheetId) {
    const sheets = this.#sheets;
    if (!sheets) return;

    /** @type {HTMLElement | null} */
    let opened = null;

    for (const panel of sheets.querySelectorAll('[data-sheet]')) {
      if (!(panel instanceof HTMLElement)) continue;

      const isTarget = panel.dataset.sheet === sheetId;
      panel.hidden = !isTarget;

      if (isTarget) opened = panel;
    }

    sheets.hidden = opened == null;
    opened?.focus();
  }

  /**
   * Closes the dock. Every panel is hidden, so nothing focusable is left
   * inside a collapsed region for Tab to reach.
   */
  closeSheet() {
    const sheets = this.#sheets;
    if (!sheets) return;

    sheets.hidden = true;

    for (const panel of sheets.querySelectorAll('[data-sheet]')) {
      if (panel instanceof HTMLElement) panel.hidden = true;
    }
  }

  /**
   * Resolves the selected length + size pair against the panel's variant map
   * and either swaps the line's variant, keeping its quantity, or adds the
   * cross-sell product the panel belongs to.
   *
   * @param {string} sheetId - The panel key.
   * @returns {Promise<void>}
   */
  async confirmSheet(sheetId) {
    if (this.#changingSize || this.hasAttribute('aria-busy')) return;
    const panel = this.#sheets?.querySelector(`[data-sheet="${CSS.escape(sheetId)}"]`);
    if (!(panel instanceof HTMLElement) || !panel.dataset.variants) return;

    /** @type {SheetVariant[]} */
    const variants = JSON.parse(panel.dataset.variants);
    const size = panel.querySelector('input[data-axis="size"]:checked');
    const length = panel.querySelector('input[data-axis="length"]:checked');

    if (!(size instanceof HTMLInputElement)) return;

    const chosenLength = length instanceof HTMLInputElement ? length.value : null;
    const match = variants.find(
      (variant) => variant.size === size.value && (variant.length === null || variant.length === chosenLength)
    );

    if (!match?.available) return;

    const { currentId, quantity } = panel.dataset;
    const moving = Number(quantity ?? 1) || 1;

    /** @type {Record<string, number>} */
    const updates = {};

    // Zero the old line first, then set the new one. When the shopper reselects
    // the size they already had, the second write lands on the same key and the
    // line is left exactly as it was rather than removed.
    if (currentId) updates[currentId] = 0;

    // `updates` keyed by variant id is ABSOLUTE, so a swap onto a size the bag
    // already holds must add to it, not replace it. 7 Sep 2026 launch audit:
    // 2×XS + 1×S, XS changed to S, came back as 2×S — the shopper's S was
    // silently lost, and with it the $200 automatic offer. Only rows this
    // section rendered for that variant count; a BOGOS gift is a 100%-off clone
    // with its own variant id, so it never collides.
    const held = String(match.id) === String(currentId) ? 0 : this.#quantityHeld(match.id);
    updates[match.id] = held + moving;

    this.closeSheet();

    if (String(match.id) === String(currentId)) return;
    this.#changingSize = true;
    const row = [...this.querySelectorAll('.echt-cart-drawer__line')]
      .find(item => item.dataset.variantId === String(currentId));
    const sizeLabel = row?.querySelector('[data-line-size]');
    sizeLabel?.setAttribute('aria-busy', 'true');
    clearTimeout(this.#sizeFeedbackTimer);
    this.#sizeFeedbackObserver?.disconnect();
    this.#sizeFeedbackScrollController?.abort();
    this.querySelector('[data-size-feedback]')?.remove();
    try {
      await this.#updateCart({ updates }, currentId ? 'update' : 'add', {
        onSuccess: () => this.#sizeFeedback(`Size updated to ${match.size}${match.length ? ` / ${match.length}` : ''}.`, 'success'),
        onError: (error) => this.#sizeFeedback(
          /availability|available|stock|sold out/i.test(error.message)
            ? 'Size change failed due to availability.'
            : `Size change failed: ${error.message}`,
          'error'
        ),
        write: () => swapCartSize({
          currentId, variantId: match.id, quantity: moving,
          sectionId: this.dataset.sectionId, routes: Theme.routes, path: window.location.pathname,
        }),
      });
    } finally {
      sizeLabel?.removeAttribute('aria-busy');
      this.#changingSize = false;
    }
  }

  #sizeFeedback(message, state) {
    clearTimeout(this.#sizeFeedbackTimer);
    let feedback = this.querySelector('[data-size-feedback]');
    if (!feedback) {
      feedback = document.createElement('div');
      feedback.className = 'echt-cart-drawer__size-feedback';
      feedback.setAttribute('data-size-feedback', '');
      feedback.setAttribute('role', 'status');
      feedback.setAttribute('aria-live', 'polite');
      const dock = () => {
        const footer = this.querySelector('.echt-cart-drawer__footer');
        if (!footer || !feedback.isConnected) return;
        const bottom = this.refs.dialog.getBoundingClientRect().bottom - footer.getBoundingClientRect().top;
        // Absolute children move with the dialog's scroll position, even when
        // overflow is hidden (keyboard focus can still scroll that container).
        feedback.style.bottom = `${bottom - this.refs.dialog.scrollTop}px`;
      };
      this.#sizeFeedbackObserver?.disconnect();
      this.#sizeFeedbackScrollController?.abort();
      this.#sizeFeedbackScrollController = new AbortController();
      this.refs.dialog.addEventListener('scroll', dock, { signal: this.#sizeFeedbackScrollController.signal });
      // Keep transient feedback OUTSIDE the HTML that Shopify morphs. Moving
      // it back into that tree during a morph can fight the renderer.
      this.refs.dialog.append(feedback);
      this.#sizeFeedbackObserver = new ResizeObserver(dock);
      this.#sizeFeedbackObserver.observe(this.refs.dialog);
      const watchFooter = () => {
        if (!feedback.isConnected) return;
        const footer = this.querySelector('.echt-cart-drawer__footer');
        if (footer) this.#sizeFeedbackObserver?.observe(footer);
        dock();
      };
      requestAnimationFrame(() => requestAnimationFrame(watchFooter));
      dock();
    }
    feedback.getAnimations().forEach((animation) => animation.cancel());
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reducedMotion) {
      feedback.animate(
        [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'translateY(0)' }],
        { duration: 220, easing: 'ease-out' }
      );
    }
    const hideFeedback = () => {
      clearTimeout(this.#sizeFeedbackTimer);
      this.#sizeFeedbackObserver?.disconnect();
      this.#sizeFeedbackScrollController?.abort();
      if (!feedback.isConnected) return;
      if (reducedMotion) {
        feedback.remove();
        return;
      }
      feedback.getAnimations().forEach((animation) => animation.cancel());
      feedback.animate(
        [{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(8px)' }],
        { duration: 180, easing: 'ease-in', fill: 'forwards' }
      ).finished.then(() => feedback.remove()).catch(() => {});
    };
    feedback.dataset.state = state;
    feedback.replaceChildren();
    const text = document.createElement('span');
    text.textContent = message;
    feedback.append(text);
    if (state !== 'pending') {
      const dismiss = document.createElement('button');
      dismiss.type = 'button';
      dismiss.className = 'button-unstyled';
      dismiss.textContent = '×';
      dismiss.setAttribute('aria-label', 'Dismiss size update');
      dismiss.addEventListener('click', hideFeedback);
      feedback.append(dismiss);
    }
    if (state === 'success' || state === 'error') {
      this.#sizeFeedbackTimer = setTimeout(hideFeedback, 3000);
    }
  }

  /**
   * Units of a variant already in the bag, summed over every rendered line.
   *
   * Read from the rows rather than fetched: the rows were rendered from the
   * same cart the swap is about to write against, and a read here would be one
   * more request on the store's cart endpoints, which throttle.
   *
   * @param {string | number} variantId
   * @returns {number}
   */
  #quantityHeld(variantId) {
    let total = 0;
    for (const row of this.querySelectorAll('.echt-cart-drawer__line')) {
      if (!(row instanceof HTMLElement) || row.dataset.variantId !== String(variantId)) continue;
      total += Number(row.dataset.quantity) || 0;
    }
    return total;
  }

  /**
   * Keep the row and its progress indicator visible until Shopify confirms the
   * removal. Stock updateQuantity still owns the request, rendering and errors.
   * @param {string | number} line - The line's 1-based index.
   */
  async removeLine(line) {
    if (this.#removingLine) return;
    const row = this.querySelectorAll('.echt-cart-drawer__line')[Number(line) - 1];
    const items = /** @type {any} */ (this.querySelector('cart-items-component'));
    if (!(row instanceof HTMLElement) || typeof items?.updateQuantity !== 'function') return;

    const { variantId, quantity } = row.dataset;
    const removed = variantId ? {
      id: variantId,
      quantity: Number(quantity) || 1,
      title: row.querySelector('.echt-cart-drawer__line-title')?.textContent?.trim() ?? '',
    } : null;
    const previous = new Map();
    const shadowRoots = new Set();
    const observer = new MutationObserver(() => markControls());
    const markButton = (control, checkout = false) => {
      if (!(control instanceof HTMLButtonElement) || previous.has(control)) return;
      const state = { disabled: control.disabled, busy: control.getAttribute('aria-busy'), position: control.style.position, spinner: null };
      previous.set(control, state);
      control.disabled = true;
      control.setAttribute('aria-busy', 'true');
      const label = control.querySelector('[data-remove-label]');
      if (label instanceof HTMLElement) {
        state.label = label;
        state.labelVisibility = label.style.visibility;
        label.style.visibility = 'hidden';
      }
      // Inline SVG also renders inside ReDo's shadow DOM, where theme CSS cannot reach.
      const spinner = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      spinner.setAttribute('viewBox', '0 0 24 24');
      spinner.setAttribute('aria-hidden', 'true');
      spinner.setAttribute('data-cart-removal-spinner', '');
      spinner.style.cssText = 'position:absolute;inset-inline-start:var(--space-4, 1rem);top:calc(50% - 0.5em);width:1em;height:1em;pointer-events:none';
      if (!checkout) spinner.style.insetInlineStart = 'calc(50% - 0.5em)';
      spinner.innerHTML = '<circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2" stroke-dasharray="42 15" />';
      if (getComputedStyle(control).position === 'static') control.style.position = 'relative';
      control.append(spinner);
      spinner.animate([{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }], {
        duration: matchMedia('(prefers-reduced-motion: reduce)').matches ? 1500 : 700,
        iterations: Infinity,
      });
      state.spinner = spinner;
    };
    const visit = (root) => {
      for (const element of root.querySelectorAll('*')) {
        if (element.matches('button:not([aria-haspopup])')) markButton(element, true);
        if (element.shadowRoot) {
          if (!shadowRoots.has(element.shadowRoot)) {
            shadowRoots.add(element.shadowRoot);
            observer.observe(element.shadowRoot, { childList: true, subtree: true });
          }
          visit(element.shadowRoot);
        }
      }
    };
    const markControls = () => {
      markButton(row.querySelector('[data-remove-line]'));
      const plate = this.querySelector('.echt-cart-drawer__plate');
      if (plate) visit(plate);
    };
    // Capture also guards keyboard submits and checkout controls inserted by ReDo.
    const blockInteraction = (event) => {
      if (!(event.target instanceof Element) || !event.target.closest('cart-items-component')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    let pending;
    const captureUpdate = (event) => { pending = event.promise; };
    this.#removingLine = true;
    this.setAttribute('data-removing-line', '');
    markControls();
    observer.observe(this, { childList: true, subtree: true });
    this.addEventListener('click', blockInteraction, true);
    this.addEventListener('submit', blockInteraction, true);
    items.addEventListener(StandardEvents.cartLinesUpdate, captureUpdate, { once: true });
    try {
      items.updateQuantity({ line: Number(line), quantity: 0, action: 'clear' });
      if (!pending) throw new Error('Cart update did not provide a completion promise');
      await pending;
      this.#removed = removed;
      if (removed) this.#showToast(`Removed ${removed.title}`.trim());
    } catch (error) {
      this.#showToast('Could not remove this item. Please try again.', { undo: false });
    } finally {
      items.removeEventListener(StandardEvents.cartLinesUpdate, captureUpdate);
      this.removeEventListener('click', blockInteraction, true);
      this.removeEventListener('submit', blockInteraction, true);
      this.#removingLine = false;
      this.removeAttribute('data-removing-line');
      observer.disconnect();
      for (const [control, state] of previous) {
        control.disabled = state.disabled;
        if (state.busy === null) control.removeAttribute('aria-busy');
        else control.setAttribute('aria-busy', state.busy);
        control.style.position = state.position;
        state.spinner?.remove();
        if (state.label) state.label.style.visibility = state.labelVisibility;
      }
    }
  }

  /**
   * Puts the last removed line back: same variant, same quantity.
   *
   * The Cart API cannot insert at an index, so the restored line lands at the
   * end of the bag rather than where it was. That is the one part of "the exact
   * line" the platform does not allow; everything else is restored.
   *
   * @returns {Promise<void>}
   */
  async undoRemove() {
    const removed = this.#removed;
    if (!removed) return;

    this.dismissToast();

    await this.#updateCart({ updates: { [removed.id]: removed.quantity } }, 'add');
  }

  /**
   * Hides the toast and drops the snapshot, so a stale Undo can never fire.
   * Bound to the toast's own close control and to the drawer closing.
   */
  dismissToast() {
    clearTimeout(this.#toastTimer);

    const { toast, toastText } = this.refs;
    if (!toast) return;

    // Hiding the element focus is inside would drop focus to the document, and
    // in a modal dialog that is a dead end for the keyboard. Hand it back to
    // the panel's close button first.
    if (toast.contains(document.activeElement)) {
      /** @type {HTMLElement | null} */ (this.querySelector('.echt-cart-drawer__close'))?.focus();
    }

    toast.hidden = true;
    if (toastText) toastText.textContent = '';
    this.#removed = null;
  }

  /**
   * Shows the toast. It never takes focus and never traps it — the undo button
   * is simply in the modal's tab order — and the `role="status"` region around
   * it is permanent, so this text change is what gets announced.
   *
   * @param {string} message - What was removed.
   */
  #showToast(message, { undo = true } = {}) {
    const { toast, toastText, toastUndo } = this.refs;
    if (!toast || !toastText) return;

    // Undo only when there is something to undo: a lapsed gift is taken out by
    // the threshold, not the shopper, and putting it back would be charging them.
    if (toastUndo) toastUndo.hidden = !undo;
    toastText.textContent = message;
    toast.hidden = false;
    this.#armToastTimer();
  }

  /** (Re)starts the auto-dismiss countdown. */
  #armToastTimer() {
    clearTimeout(this.#toastTimer);
    this.#toastTimer = setTimeout(() => this.dismissToast(), TOAST_MS);
  }

  /**
   * A gift-picker card was submitted: add its chosen variant without leaving
   * the drawer.
   *
   * @param {SubmitEvent} event
   */
  #onGiftSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-echt-gift-add')) return;
    event.preventDefault();

    const id = new FormData(form).get('id');
    if (!id) return;
    this.#updateCart({ updates: { [String(id)]: 1 } }, 'add');
  };

  /**
   * Re-reads the cart after a write this component did not make, and did not
   * hear about either.
   *
   * BOGOS (snippets/echt-bogos-sync.liquid) adds and removes its gift line with
   * its own fetch and never dispatches `CartLinesUpdateEvent`, so neither
   * `<cart-items-component>` nor the meter would learn about it — the drawer
   * would go on showing the pre-gift bag until the next navigation.
   *
   * The write is `{updates: {}}`: a no-op mutation whose RESPONSE is the whole
   * cart plus this section re-rendered. That is deliberate. Fetching /cart.js
   * here instead would give us a second, unsynchronised copy of cart state to
   * reconcile; going through #updateCart keeps exactly one path, so the lines
   * morph, cart-icon re-counts and ReDo is nudged, all off one response.
   *
   * `silent` because the gift in question is the app's: BOGOS removes it
   * itself when the bag drops under the threshold, and #dropLapsedGift only
   * ever touches products listed in `data-gift-products` (the native picker's
   * catalogue, empty while BOGOS runs), so the two cannot fight over a line.
   *
   * @param {{open?: boolean}} [options] - `open` also opens the drawer, which is
   *   what live does when the app reports a gift added.
   * @returns {Promise<void>}
   */
  async syncExternalCartWrite(options = {}) {
    if (options.open) this.open();
    await this.#updateCart({ updates: {} }, 'update', { silent: true });
  }

  /**
   * Takes the free gift back out of a bag that has dropped under the gift
   * threshold — what BOGOS does on live (`fg-cart:auto-updated`). Without it
   * the $0 discount lapses and the gift silently becomes a full-price line.
   *
   * Only products named in `data-gift-products` — the native picker's own
   * catalogue — are ever removed. A BOGOS gift line is a clone product that is
   * not in that list (and the list is empty while BOGOS runs), so this never
   * takes out the app's gift; BOGOS drops that itself.
   *
   * The basis is the same one the meter uses (items subtotal, protection line
   * out) with the gift's own line out too, so a gift that is being charged for
   * cannot count towards keeping itself.
   *
   * @param {{items?: Array<{id: number, product_id: number, final_line_price: number}>, items_subtotal_price?: number}} cart
   * @returns {Promise<boolean>} Whether a gift was removed.
   */
  async #dropLapsedGift(cart) {
    const threshold = Number(this.dataset.giftThreshold || 0);
    const giftIds = (this.dataset.giftProducts || '').split(',').filter(Boolean);
    if (!threshold || !giftIds.length || !Array.isArray(cart.items)) return false;

    const protection = String(this.dataset.protectionVariant || '');
    let basis = Number(cart.items_subtotal_price || 0);
    let gift = null;
    for (const item of cart.items) {
      if (giftIds.includes(String(item.product_id))) {
        gift = gift ?? item;
        basis -= item.final_line_price;
      } else if (String(item.id) === protection) {
        basis -= item.final_line_price;
      }
    }
    if (!gift || basis >= threshold) return false;

    await this.#updateCart({ updates: { [String(gift.id)]: 0 } }, 'remove', { silent: true });
    this.#showToast('Free gift removed — your bag is under the gift threshold', { undo: false });
    return true;
  }

  /**
   * Adds a one-size cross-sell product straight to the bag.
   *
   * @param {string | number} variantId - The variant to add.
   * @returns {Promise<void>}
   */
  #railAddQueue = Promise.resolve();

  async addFromRail(variantId) {
    const selector = `[data-rail-variant="${CSS.escape(String(variantId))}"]`;
    const button = this.querySelector(selector);
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    delete button.dataset.added;
    button.setAttribute('aria-label', 'Adding to bag');
    let confirmed = false;

    // Serialize different rail selections so a second tap cannot abort the
    // first write or render a cart response that predates it.
    const pending = this.#railAddQueue.then(() => this.#updateCart({ updates: { [variantId]: 1 } }, 'add', {
      onSuccess: () => {
        confirmed = true;
        button.removeAttribute('aria-busy');
        button.dataset.added = 'true';
        button.setAttribute('aria-label', 'Added to bag');
      },
    }));
    this.#railAddQueue = pending.catch(() => {});
    try {
      await pending;
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      const reset = () => {
        delete button.dataset.added;
        button.removeAttribute('aria-label');
      };
      if (confirmed) setTimeout(reset, 2000);
      else reset();
    }
  }

  /**
   * Opts into package protection. The price tier was resolved server-side, so
   * this only adds the variant the section named and records the attribute the
   * live store's own scripts read.
   *
   * @returns {Promise<void>}
   */
  async addProtection() {
    const variantId = this.dataset.protectionVariant;
    if (!variantId) return;

    await this.#updateCart({ updates: { [variantId]: 1 }, attributes: { _insurance: 'yes' } }, 'add');
  }

  /**
   * Declines package protection and re-totals.
   *
   * @returns {Promise<void>}
   */
  async removeProtection() {
    const variantId = this.dataset.protectionVariant;
    if (!variantId) return;

    await this.#updateCart({ updates: { [variantId]: 0 }, attributes: { _insurance: '' } }, 'remove');
  }

  /**
   * One write path for every mutation this component owns.
   *
   * `/cart/update.js` rather than `/cart/add.js` so a variant swap, an add and
   * a removal are each one request whose response is the whole cart — which is
   * what `CartLinesUpdateEvent` needs to hand a correct cart to `cart-icon`
   * and a freshly rendered section to `cart-items-component`.
   *
   * @param {{updates: Record<string, number>, attributes?: Record<string, string>}} payload - Cart AJAX payload.
   * @param {'add' | 'update' | 'remove'} action - What the shopper did, for listeners.
   * @param {{silent?: boolean, onSuccess?: () => void, onError?: (error: Error) => void, write?: () => Promise<any>}} [options] - `silent` skips the lapsed-gift check, which
   *   is only ever needed for the removal that check itself issues.
   * @returns {Promise<void>}
   */
  async #updateCart(payload, action, options = {}) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    this.#activeFetch?.abort();

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    const body = JSON.stringify({
      ...payload,
      sections: sectionId,
      sections_url: window.location.pathname,
    });

    const deferred = CartLinesUpdateEvent.createPromise();
    const lines = Object.entries(payload.updates).map(([id, quantity]) => ({ id, quantity }));

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action,
        context: 'cart',
        lines,
        promise: deferred.promise,
      })
    );

    this.setAttribute('aria-busy', 'true');
    let lastCart = null;

    try {
      const response = options.write ? null : await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body }),
        signal: abortController.signal,
      });

      if (response && !response.ok) throw new Error(`Cart update failed: ${response.status} ${response.statusText}`);

      const data = options.write ? await options.write() : await response.json();
      lastCart = data;
      // The whole cart came back, codes included — keep the chip cache current
      // without a second read (see EchtCartDiscount#onCartLinesUpdate).
      if (Array.isArray(data.discount_codes)) knownDiscountCodes = normaliseDiscountCodes(data.discount_codes);

      options.onSuccess?.();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        detail: {
          sections: data.sections,
          items: data.items,
          itemCount: data.item_count,
          source: 'echt-cart-drawer',
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);

      if (error instanceof Error && error.name !== 'AbortError') {
        options.onError?.(error);
        // Size changes already have their own docked, auto-dismissing feedback.
        if (!options.onError) this.#showToast('Could not update your bag. Please try again.', { undo: false });
        console.warn('[echt-cart-drawer] Cart update failed:', error);
      }
    } finally {
      this.removeAttribute('aria-busy');

      if (this.#activeFetch === abortController) this.#activeFetch = null;
    }

    // After the morph, not before it: the removal is its own write through
    // this same path, and issuing it mid-flight would abort the request that
    // is still being resolved. The full-price line is on screen for one
    // round trip, which is what BOGOS's `fg-cart:auto-updated` refresh does too.
    if (!options.silent && lastCart) await this.#dropLapsedGift(lastCart);
  }
}

if (!customElements.get('echt-cart-drawer')) customElements.define('echt-cart-drawer', EchtCartDrawer);

/**
 * The discount codes the CART is holding, as the last cart response reported
 * them — `[{code, applicable}]`, Shopify's own shape.
 *
 * Module scope, not a field on the element, because the panel is morphed on
 * every cart write: an element that gets replaced would lose the set, and the
 * whole reason for keeping it is that some of these codes cannot be rendered
 * from Liquid at all. `null` means "we have not asked the server yet"; `[]`
 * means "asked, none".
 *
 * @type {{code: string, applicable: boolean}[] | null}
 */
let knownDiscountCodes = null;

/**
 * `<echt-cart-discount>` — the drawer's discount code field and applied-code
 * chips. Stock's markup, stock's `.cart-discount__*` class names, our element.
 *
 * WHY THIS EXISTS. Stock `cart-discount.js` derives the set of codes on the
 * cart from the pills in the DOM (`#existingDiscounts()` reads
 * `.cart-discount__pill`), and the pills are rendered from the cart's discount
 * APPLICATIONS. Those are not the same set. Measured on this store, 7 Sep 2026,
 * with 3× Elemental Flow Pants in the bag:
 *
 *   POST /cart/update.js {"discount":"3FOR30"}        -> [{3FOR30, applicable:true}]
 *   POST /cart/update.js {"discount":"3FOR30,SAVE20"} -> [{3FOR30, true}, {SAVE20, false}]
 *
 * The cart is holding both. Checkout lists both. But SAVE20 discounts nothing,
 * so it has no application, so it renders no pill — and the next apply, whose
 * payload is built from the pills, silently drops it. That is the client report:
 * "Seems like multiple discount codes don't appear at the cart — appears at
 * checkout however" (Jeremy, 7 Sep 2026). A free-shipping code behaves the same
 * way, and stock reads that case as an error ("Discount not applicable for
 * shipping") because the pill it expected never appeared.
 *
 * So the set comes from the cart response's own `discount_codes` instead, and
 * the chips are reconciled against it: every code on the cart gets a chip and a
 * remove control, the ones Shopify is not applying say so rather than being
 * hidden. Apply sends the union, remove sends the set minus one — both from
 * that set, never from the DOM.
 *
 * The `discount` field of /cart/update.js REPLACES the whole set (verified: a
 * bare `{"discount":"X"}` drops everything else), which is what makes the union
 * load-bearing rather than a nicety.
 *
 * Server-rendered chips are left alone. A chip Liquid rendered is proof of an
 * application, so it is applied by definition, whatever a stale cached set
 * says; only chips this file added carry `data-applicable`.
 *
 * @typedef {object} EchtCartDiscountRefs
 * @property {HTMLElement} codeList - The chip list.
 * @property {HTMLElement} cartDiscountError - The notice row.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The invalid-code message.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping message.
 *
 * @extends {Component<EchtCartDiscountRefs>}
 */
class EchtCartDiscount extends Component {
  requiredRefs = ['codeList', 'cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {AbortController | null} */
  #listeners = null;

  connectedCallback() {
    super.connectedCallback();

    // A morph can replace this element while the cached set is still good.
    this.#renderCodes();

    // Every other cart write — stock's quantity steppers, a line removal, the
    // drawer's size swap, BOGOS adding a gift — morphs this section from
    // Liquid, which can only render the codes that are APPLYING. 7 Sep 2026
    // launch audit: three held codes shown, one quantity change later only the
    // applied one was left, and the shopper had no way to remove the others.
    this.#listeners = new AbortController();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate, {
      signal: this.#listeners.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#activeFetch?.abort();
    this.#activeFetch = null;
    this.#listeners?.abort();
    this.#listeners = null;
  }

  /**
   * Puts the chips back after someone else's cart write has morphed them away.
   *
   * A quantity change can also move a code between applying and not (3FOR30
   * needs three eligible lines), so the cached set is stale, not just hidden:
   * the drawer's own writes and product-form updates supply their response;
   * other writers cost one `/cart.js` read. If it fails the previous set is kept —
   * chips that may be one state out beat chips that are gone.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #onCartLinesUpdate = (event) => {
    event.promise
      ?.then(async ({ detail }) => {
        if (detail?.didError) return;
        if (detail?.source !== 'echt-cart-drawer') {
          const previous = knownDiscountCodes;
          knownDiscountCodes = null;
          await this.refresh(detail);
          if (!knownDiscountCodes) knownDiscountCodes = previous;
        }
        // The morph runs in another listener's `.then` on this same promise;
        // yield once so the chips are rebuilt on the section that is staying.
        await new Promise((resolve) => setTimeout(resolve, 0));
        this.#renderCodes();
      })
      .catch(() => {});
  };

  /**
   * Reads the cart once, so codes with no discount application can be shown.
   *
   * Called from `EchtCartDrawer.open()` rather than on connect: it is one more
   * request on every page load otherwise, against the project's first goal, and
   * nobody can see the chips until the drawer is open. After the first read our
   * own writes keep the set current, so this is a no-op from then on.
   *
   * @returns {Promise<void>}
   */
  async refresh(detail) {
    if (knownDiscountCodes) return;

    try {
      const cart = await cartForUpdate(detail);
      knownDiscountCodes = normaliseDiscountCodes(cart.discount_codes);
      this.#renderCodes();
    } catch (error) {
      // A failed read leaves the server-rendered chips exactly as they are,
      // which is the pre-7-Sep behaviour — never worse than not asking.
      if (error instanceof Error && error.name !== 'AbortError') {
        console.warn('[echt-cart-discount] Could not read the cart:', error);
      }
    }
  }

  /**
   * Applies the typed code, keeping every code already on the cart.
   *
   * @param {SubmitEvent} event - The form's submit.
   * @returns {Promise<void>}
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    const input = form instanceof HTMLFormElement ? form.querySelector('input[name="discount"]') : null;
    if (!(input instanceof HTMLInputElement)) return;

    const typed = input.value.trim();
    if (!typed) return;

    this.#hideError();

    const existing = this.#cartCodes();
    // Already on the cart: nothing to send, and re-sending would read as a
    // second application of the same code.
    if (existing.some((code) => code.toLowerCase() === typed.toLowerCase())) {
      input.value = '';
      return;
    }

    const data = await this.#write([...existing, typed]);
    if (!data) return;

    const applied = knownDiscountCodes?.find((entry) => entry.code.toLowerCase() === typed.toLowerCase());

    if (!applied || !applied.applicable) {
      // "Codes never silently drop" — the brief. The value stays in the field
      // so a typo can be corrected rather than retyped from memory, and the
      // section is deliberately NOT morphed: nothing about the totals moved,
      // and a morph would re-render the notice row hidden. The chip for the
      // code the cart is now holding is added by #renderCodes.
      this.#showError('discount_code');
      this.#renderCodes();
      return;
    }

    input.value = '';
    await this.#morph(data);
  };

  /**
   * Takes one code off, keeping the rest.
   *
   * @param {MouseEvent} event - The click on a chip's cross.
   * @returns {Promise<void>}
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const target = event.target;
    const pill = target instanceof Element ? target.closest('.cart-discount__pill') : null;
    const code = pill instanceof HTMLElement ? pill.dataset.discountCode : null;
    if (!code) return;

    this.#hideError();

    const remaining = this.#cartCodes().filter((entry) => entry.toLowerCase() !== code.toLowerCase());
    const data = await this.#write(remaining);
    if (data) await this.#morph(data);
  };

  /**
   * Every code the cart is holding, best known: the cached set when we have one,
   * the server-rendered chips before the first response of the page.
   *
   * @returns {string[]}
   */
  #cartCodes() {
    if (knownDiscountCodes) return knownDiscountCodes.map((entry) => entry.code);

    return [...this.querySelectorAll('.cart-discount__pill')]
      .map((pill) => (pill instanceof HTMLElement ? pill.dataset.discountCode : null))
      .filter((code) => typeof code === 'string' && code !== '');
  }

  /**
   * Writes a whole code set to the cart and caches what comes back.
   *
   * @param {string[]} codes - The complete set the cart should hold.
   * @returns {Promise<any | null>} The cart response, or null if it failed.
   */
  async #write(codes) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return null;

    this.#activeFetch?.abort();

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    const deferred = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: codes.map((code) => ({ code })),
        promise: deferred.promise,
      })
    );

    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', {
          // Comma-separated, and it REPLACES the set — so this string is always
          // the whole cart's codes, never just the one that changed.
          // sections_url as well as sections, matching #updateCart. Without it
          // Shopify renders the section against `/`, and this drawer's rail and
          // threshold read `template` and `routes` — so the same cart could come
          // back rendered differently depending on which write produced it.
          body: JSON.stringify({
            discount: codes.join(','),
            sections: [sectionId],
            sections_url: window.location.pathname,
          }),
        }),
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error(`Discount update failed: ${response.status} ${response.statusText}`);

      const data = await response.json();
      knownDiscountCodes = normaliseDiscountCodes(data.discount_codes);

      deferred.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data) });

      return data;
    } catch (error) {
      deferred.reject(error);

      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'SERVICE_UNAVAILABLE' }));
      }

      return null;
    } finally {
      this.removeAttribute('aria-busy');

      if (this.#activeFetch === abortController) this.#activeFetch = null;
    }
  }

  /**
   * Morphs the section from the response, then re-asserts the chips.
   *
   * `hydration`, not stock's `full`. Stock picks the mode by asking whether it
   * is inside a `<theme-drawer>`; this drawer is not one, so stock re-morphed
   * the whole section — including the `<dialog>` the shopper is standing in —
   * where every other write in this panel morphs only the subtree under
   * `data-hydration-key`. One drawer, one morph mode.
   *
   * A response claiming an EMPTY cart is never morphed in on the strength of
   * one read. A discount write cannot remove a line — the payload is `discount`
   * and `sections`, never `updates` — so `item_count: 0` against a drawer that
   * is showing items is a contradiction, and the cart endpoints are the wrong
   * place to take one on trust. Measured 7 Sep 2026 on the dev server while
   * Shopify was throttling this machine: the panel was server-rendered with
   * three items and a $210.00 subtotal, while an in-page `/cart.js` read
   * answered `item_count: 0` with no codes. Both applies in that session took
   * the rejection branch, which does not morph, so the drawer kept its
   * server-rendered contents; the remove was the first write to reach this
   * method, and it morphed the panel to "YOUR BAG (0) — YOUR BAG IS EMPTY"
   * with the bag in fact still full. The empty state is
   * `{%- if cart.empty? -%}` in the section, so that HTML could only have come
   * from the server: the response body was empty, not the cart.
   *
   * So: re-read the section once, from a plain GET rather than another write,
   * and morph THAT. If the bag really is empty the second read says so too and
   * the empty state renders a beat later; if it was an artefact the panel keeps
   * its contents. Either way nothing is written twice.
   *
   * @param {any} data - The cart response, carrying `sections`.
   * @returns {Promise<void>}
   */
  async #morph(data) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    let html = data?.sections?.[sectionId];

    if (data?.item_count === 0 && this.#itemCount() > 0) {
      console.warn(
        `[echt-cart-discount] /cart/update.js answered item_count 0 for a discount write while the drawer ` +
          `is showing ${this.#itemCount()} item(s). Re-reading the cart before rendering an empty bag.`
      );
      html = await this.#rerenderSection(sectionId);
    }

    if (!html) {
      // Nothing trustworthy to render. The chips are still put right — they
      // come from the code set, not from the section HTML — so the shopper sees
      // the code go, and the totals catch up on the next cart interaction.
      this.#renderCodes();
      return;
    }

    morphSection(sectionId, html, { mode: 'hydration' });
    this.#renderCodes();
  }

  /**
   * The item count the drawer is currently showing.
   *
   * The section prints it into `ref="cartItemCount"` for exactly this kind of
   * question. `-1` when there is nothing to read, which never satisfies the
   * `> 0` test above — an unreadable count must not turn the guard on.
   *
   * @returns {number}
   */
  #itemCount() {
    const node = this.querySelector('[ref="cartItemCount"]');
    const count = node ? Number.parseInt(node.textContent?.trim() ?? '', 10) : Number.NaN;

    return Number.isNaN(count) ? -1 : count;
  }

  /**
   * Re-renders this section with a GET, for the second opinion above.
   *
   * A GET, not a second `/cart/update.js`: the write already happened and
   * repeating it under a throttle is how a retry loop starts.
   *
   * @param {string} sectionId - The section to render.
   * @returns {Promise<string | null>} The section HTML, or null.
   */
  async #rerenderSection(sectionId) {
    try {
      const response = await fetch(
        `${Theme.routes.cart_url}?sections=${encodeURIComponent(sectionId)}`,
        { headers: { Accept: 'application/json' } }
      );
      if (!response.ok) return null;

      const sections = await response.json();

      return sections?.[sectionId] ?? null;
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.warn('[echt-cart-discount] Could not re-read the section:', error);
      }

      return null;
    }
  }

  /**
   * Brings the chip list level with the cart: a chip per code the cart holds,
   * the ones Shopify is not applying marked as such.
   *
   * The list is queried rather than read off `this.refs`, because refs are
   * refreshed by a MutationObserver and this runs in the same tick as a morph.
   */
  #renderCodes() {
    const list = this.querySelector('.echt-cart-drawer__codes');
    if (!(list instanceof HTMLElement) || !knownDiscountCodes) return;

    /** @type {Map<string, HTMLElement>} */
    const rendered = new Map();
    /** @type {Map<string, HTMLElement>} */
    const added = new Map();

    for (const pill of list.querySelectorAll('.cart-discount__pill')) {
      if (!(pill instanceof HTMLElement) || !pill.dataset.discountCode) continue;
      const key = pill.dataset.discountCode.toLowerCase();
      if (pill.dataset.echtAdded === undefined) rendered.set(key, pill);
      else added.set(key, pill);
    }

    for (const { code, applicable } of knownDiscountCodes) {
      const key = code.toLowerCase();
      // A server-rendered chip is proof of a discount application, so it is
      // applied whatever a cached flag says. Leave it exactly as Liquid wrote it.
      if (rendered.has(key)) continue;

      const pill = added.get(key) ?? this.#buildPill(code, list);
      added.delete(key);
      this.#setApplicable(pill, applicable);
    }

    // Anything this file added for a code the cart no longer holds.
    for (const pill of added.values()) pill.remove();
  }

  /**
   * Marks a chip applied or not applied.
   *
   * @param {HTMLElement} pill - The chip.
   * @param {boolean} applicable - Whether Shopify is applying the code.
   */
  #setApplicable(pill, applicable) {
    pill.dataset.applicable = String(applicable);

    const existing = pill.querySelector('.echt-cart-drawer__code-state');

    if (applicable) {
      existing?.remove();
      return;
    }

    // The chip's own aria-label says "…applied", which this one is not; the
    // visible text reads correctly on its own once the label is gone.
    pill.removeAttribute('aria-label');

    if (existing) return;

    const note = document.createElement('span');
    note.className = 'echt-cart-drawer__code-state';
    // Hardcoded English, like the eight other strings this section cannot
    // translate — locales/*.json are stock files and carry no key for it.
    note.textContent = 'not applied';
    pill.querySelector('.cart-discount__pill-code')?.after(note);
  }

  /**
   * Builds a chip for a code Liquid could not render.
   *
   * Same markup as the section's own chip, down to the cross, so the two are
   * indistinguishable to CSS and to the shopper. `on:click` is an attribute the
   * framework reads off a delegated document listener, so setting it here is
   * enough to wire the remove control.
   *
   * @param {string} code - The discount code.
   * @param {HTMLElement} list - The chip list to append to.
   * @returns {HTMLElement} The chip.
   */
  #buildPill(code, list) {
    const pill = document.createElement('li');
    pill.className = 'cart-discount__pill echt-cart-drawer__code';
    pill.dataset.discountCode = code;
    pill.dataset.echtAdded = '';

    const label = document.createElement('span');
    label.className = 'cart-discount__pill-code';
    label.textContent = code;

    const remove = document.createElement('button');
    remove.className = 'echt-cart-drawer__code-remove';
    remove.type = 'button';
    remove.setAttribute('on:click', '/removeDiscount');
    remove.setAttribute('aria-label', `Remove discount code ${code}`);
    remove.innerHTML =
      '<svg viewBox="0 0 10 10" aria-hidden="true" focusable="false"><path d="M1 1 L9 9 M9 1 L1 9"/></svg>';

    pill.append(label, remove);
    list.append(pill);

    return pill;
  }

  /**
   * Shows one of the notice row's two messages.
   *
   * Only `discount_code` is ever raised now. Stock reached for the shipping
   * message when a code applied but produced no pill, which on this store is a
   * free-shipping code working correctly — the chip list says so instead. The
   * message and its ref stay because they are stock's contract and the cart
   * page still renders them.
   *
   * @param {'discount_code' | 'shipping'} type - Which message to show.
   */
  #showError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;

    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: type === 'discount_code' ? 'Invalid discount code' : 'Discount not applicable for shipping',
        code: 'VALIDATION_CUSTOM',
      })
    );
  }

  /** Hides the notice row and both of its messages. */
  #hideError() {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');
  }
}

/**
 * Normalises the cart's `discount_codes` into the shape this file relies on.
 * The field is absent on some cart responses and its entries are only ever
 * `{code, applicable}`, so anything unrecognisable is dropped rather than
 * carried as a half-built chip.
 *
 * @param {unknown} codes - The `discount_codes` field of a cart response.
 * @returns {{code: string, applicable: boolean}[]}
 */
function normaliseDiscountCodes(codes) {
  if (!Array.isArray(codes)) return [];

  return codes
    .filter((entry) => entry && typeof entry.code === 'string' && entry.code !== '')
    .map((entry) => ({ code: entry.code, applicable: entry.applicable !== false }));
}

if (!customElements.get('echt-cart-discount')) customElements.define('echt-cart-discount', EchtCartDiscount);
